'use client';

import { useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter, Link } from '../../../i18n/routing';
import { Mail } from 'lucide-react';
import Image from 'next/image';

export default function ForgotPasswordPage() {
  const t = useTranslations();
  const _locale = useLocale();
  const _router = useRouter();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const validateEmail = (value: string): boolean => {
    return !!value && /.+@.+\..+/.test(value);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailError(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      setEmailError(t('forgotPassword.emailInvalid') || 'Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { requestPasswordReset } = await import('@/actions/auth');
      const result = await requestPasswordReset(email);

      if (result.success) {
        setEmailSent(true);
      } else {
        setError(result.error || t('forgotPassword.requestFailed') || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Password reset request failed:', error);
      setError(t('forgotPassword.requestFailed') || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 font-sans">
        {/* LEFT SIDE: Image with Overlay */}
        <div className="hidden lg:flex relative min-h-screen overflow-hidden">
          <Image
            src="/login-bg.jpg"
            alt="Sailing adventure"
            fill
            priority
            className="object-cover"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/50" />
          {/* Quote Overlay */}
          <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-center leading-tight">
              {t('loginPage.oceanQuote')}
            </h2>
            <p className="text-lg lg:text-xl text-white/90 text-center max-w-md">
              {t('loginPage.heroSubheadline')}
            </p>
          </div>
        </div>

        {/* RIGHT SIDE: Success Message */}
        <div className="flex flex-col justify-center items-center p-6 sm:p-8 lg:p-12 bg-white dark:bg-slate-50 min-h-screen">
          <div className="w-full max-w-md">
            {/* Mobile Header Image */}
            <div className="lg:hidden relative h-48 mb-8 rounded-xl overflow-hidden">
              <Image
                src="/login-bg.jpg"
                alt="Sailing adventure"
                fill
                className="object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60" />
              <div className="absolute inset-0 flex items-center justify-center p-6">
                <h2 className="text-2xl font-bold text-white text-center">
                  {t('loginPage.oceanQuote')}
                </h2>
              </div>
            </div>

            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                <Mail className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-900 mb-2">
                {t('forgotPassword.emailSent') || 'Check Your Email'}
              </h1>
              <p className="text-slate-600 dark:text-slate-600">
                {t('forgotPassword.emailSentDescription') || `We've sent a password reset link to ${email}. Please check your inbox and follow the instructions.`}
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>{t('forgotPassword.magicLinkInfo') || 'Magic Link:'}</strong> {t('forgotPassword.magicLinkDescription') || 'Click the link in the email to reset your password. The link will expire in 1 hour.'}
              </p>
            </div>

            <div className="space-y-3">
              <Link
                href="/login"
                className="block w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/30 transition-all shadow-lg hover:shadow-blue-500/25 text-center"
              >
                {t('forgotPassword.backToLogin') || 'Back to Login'}
              </Link>
              <button
                onClick={() => {
                  setEmailSent(false);
                  setEmail('');
                }}
                className="block w-full px-6 py-3 border border-slate-300 dark:border-slate-300 text-slate-700 dark:text-slate-700 font-semibold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-50 transition-colors"
              >
                {t('forgotPassword.sendAnother') || 'Send Another Email'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 font-sans">
      {/* LEFT SIDE: Image with Overlay */}
      <div className="hidden lg:flex relative min-h-screen overflow-hidden">
        <Image
          src="/login-bg.jpg"
          alt="Sailing adventure"
          fill
          priority
          className="object-cover"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/50" />
        {/* Quote Overlay */}
        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-center leading-tight">
            {t('loginPage.oceanQuote')}
          </h2>
          <p className="text-lg lg:text-xl text-white/90 text-center max-w-md">
            {t('loginPage.heroSubheadline')}
          </p>
        </div>
      </div>

      {/* RIGHT SIDE: Form */}
      <div className="flex flex-col justify-center items-center p-6 sm:p-8 lg:p-12 bg-white dark:bg-slate-50 min-h-screen">
        <div className="w-full max-w-md">
          {/* Mobile Header Image */}
          <div className="lg:hidden relative h-48 mb-8 rounded-xl overflow-hidden">
            <Image
              src="/login-bg.jpg"
              alt="Sailing adventure"
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60" />
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <h2 className="text-2xl font-bold text-white text-center">
                {t('loginPage.oceanQuote')}
              </h2>
            </div>
          </div>

          {/* Form Card */}
          <div className="w-full">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-900 mb-2">
                {t('forgotPassword.title') || 'Forgot Your Password?'}
              </h1>
              <p className="text-slate-600 dark:text-slate-600">
                {t('forgotPassword.description') || 'Enter your email address and we\'ll send you a link to reset your password.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-700">
                  {t('forgotPassword.emailLabel') || 'Email Address'}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  required
                  autoComplete="email"
                  className={`w-full px-4 py-3 bg-white dark:bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-900 dark:text-slate-900 placeholder:text-slate-400 ${
                    emailError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-300'
                  }`}
                  placeholder={t('forgotPassword.emailPlaceholder') || 'your.email@example.com'}
                />
                {emailError && (
                  <p className="text-sm text-red-500 mt-1">{emailError}</p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed mt-2 flex items-center justify-center shadow-lg hover:shadow-blue-500/25"
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                {loading
                  ? (t('forgotPassword.sending') || 'Sending...')
                  : (t('forgotPassword.sendResetLink') || 'Send Reset Link')}
              </button>
            </form>

            <div className="text-center mt-4">
              <Link
                href="/login"
                className="text-base font-bold text-blue-600 dark:text-blue-600 hover:underline"
              >
                {t('forgotPassword.backToLogin') || 'Back to Login'}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

